import React, { useContext, useState } from 'react'
import moment from 'moment'
import SimpleTextField from '../../../../components/Forms/SimpleTextField'
import AnDateTimePicker from '../../../../components/DateTime/DateTimePicker'
//@MUI
import Button from '@mui/material/Button'
import FormControl from '@mui/material/FormControl'
import Grid from '@mui/material/Grid'
import InputLabel from '@mui/material/InputLabel'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Select from '@mui/material/Select'
import Typography from '@mui/material/Typography'
import AVPU from './Fields/AVPU'
import RespRate from './Fields/RespRate'
import RespDistress from './Fields/RespDistress'
import EstimatedAgeBand from './Fields/EstimatedAgeBand'
import Spo2 from './Fields/Spo2'
import SuppO2, { RespSupport, SuppO2Fio2 } from './Fields/SuppO2'
import HeartRate from './Fields/HeartRate'
import Temperature from './Fields/Temperature'
import { GcsEyes, GcsMotor, GcsVerbal } from './Fields/GCS'
import { BpDerogation, BpDiastolic, BpPosition, BpSystolic } from './Fields/BP'
import PainScore from './Fields/PainScore'
import PEFR from './Fields/PEFR'
import ETCO2 from './Fields/ETCO2'
import BGL from './Fields/BGL'
import CapRefill from './Fields/CapRefill'
import { isUnder18 } from '../../_pewsCalc'

const VitalSignsPopup = ({
  existingSet,
  handleChange,
  formValues,
  closePopup,
}) => {
  const paediatric = isUnder18(formValues?.demographics)
  const dobUnknownPaediatric =
    paediatric &&
    formValues?.demographics?.dobUnknown &&
    formValues?.demographics?.dobUnknownButPaediatric

  // Estimated age group carries over from the most recent obs set so it only
  // needs picking once per unknown-DOB patient
  const lastObs = [...(formValues?.vitals?.obs ?? [])]
    .sort((a, b) => new Date(a.time) - new Date(b.time))
    .slice(-1)[0]

  const [time, setTime] = useState(
    Object.keys(existingSet).length > 1 ? moment(existingSet.time) : moment(),
  )
  const [avpu, setAvpu] = useState(existingSet.avpu ?? '')
  const [respRate, setRespRate] = useState(existingSet.respRate ?? '')
  const [spo2, setSpo2] = useState(existingSet.spo2 ?? '')
  const [heartRate, setHeartRate] = useState(existingSet.heartRate ?? '')
  const [temp, setTemp] = useState(existingSet.temp ?? '')
  const [gcsEyes, setGcsEyes] = useState(existingSet.gcsEyes ?? '')
  const [gcsVerbal, setGcsVerbal] = useState(existingSet.gcsVerbal ?? '')
  const [gcsMotor, setGcsMotor] = useState(existingSet.gcsMotor ?? '')
  const [bpSys, setBpSys] = useState(existingSet.bpSys ?? '')
  const [bpDia, setBpDia] = useState(existingSet.bpDia ?? '')
  const [bpPosition, setBpPosition] = useState(
    existingSet?.bpPosition ?? 'Sitting',
  )
  const [bpDerogation, setBpDerogation] = useState(
    existingSet.bpDerogation ?? '',
  )
  const [painScore, setPainScore] = useState(existingSet.painScore ?? '')
  const [suppO2, setSuppO2] = useState(existingSet.suppO2 ?? '')
  const [suppO2Fio2, setSuppO2Fio2] = useState(existingSet.suppO2Fio2 ?? '')
  const [respSupport, setRespSupport] = useState(
    existingSet.respSupport ?? '',
  )
  const [respDistress, setRespDistress] = useState(
    existingSet.respDistress ?? '',
  )
  const [estimatedAgeBand, setEstimatedAgeBand] = useState(
    existingSet.estimatedAgeBand ?? lastObs?.estimatedAgeBand ?? '',
  )
  const [pefr, setPefr] = useState(existingSet.pefr ?? '')
  const [etco2, setEtco2] = useState(existingSet.etco2 ?? '')
  const [bgl, setBgl] = useState(existingSet?.bgl ?? '')
  const [capRefill, setCapRefill] = useState(existingSet?.capRefill ?? '')

  const submitObs = (e) => {
    e.preventDefault()
    let Ar = [...(formValues?.vitals?.obs ?? [])]

    const thisFormValues = {
      time,
      avpu,
      respRate,
      spo2,
      heartRate,
      temp,
      gcsEyes,
      gcsVerbal,
      gcsMotor,
      bpSys,
      bpDia,
      bpPosition,
      // Derogation only applies while there is no systolic reading
      bpDerogation: bpSys ? '' : bpDerogation,
      painScore,
      suppO2,
      suppO2Fio2,
      respSupport,
      respDistress,
      estimatedAgeBand,
      pefr,
      etco2,
      bgl,
      capRefill,
    }

    if (Object.keys(existingSet).length > 0) {
      const objIndex = Ar.findIndex((obj) =>
        moment(obj.time).isSame(existingSet.time),
      )
      // Keep bookkeeping keys (live-capture entryId / liveEventId) on edit
      Ar[objIndex] = { ...Ar[objIndex], ...thisFormValues }
    } else {
      Ar = [...Ar, thisFormValues]
    }
    handleChange({
      moduleName: 'vitals',
      field: 'obs',
      value: Ar,
    })
    closePopup()
  }

  const SectionHeader = ({ title, caption }) => (
    <Grid item xs={12}>
      <Typography variant="subtitle2">{title}</Typography>
      {caption && (
        <Typography variant="caption" color="text.secondary">
          {caption}
        </Typography>
      )}
    </Grid>
  )

  return (
    <Paper sx={{ padding: 3 }}>
      <Grid container spacing={2} component="form" onSubmit={submitObs}>
        <Grid item xs={12} lg={8}>
          <Typography variant="title3">
            {Object.keys(existingSet).length > 0 ? 'Edit' : 'New'} Vital Signs
          </Typography>
        </Grid>
        <Grid item xs={12} lg={4}>
          <AnDateTimePicker
            date={time}
            fullWidth
            onChange={(newTime) => setTime(newTime)}
          />
        </Grid>

        {paediatric ? (
          <>
            <SectionHeader
              title="PEWS Observations"
              caption="All of these are needed for a PEWS score to be calculated (NHS England National PEWS)."
            />
            {dobUnknownPaediatric && (
              <EstimatedAgeBand
                value={estimatedAgeBand}
                onChange={(e) => setEstimatedAgeBand(e.target.value)}
              />
            )}
            <RespRate
              value={respRate}
              onChange={(e) =>
                e.target.value >= 0 && setRespRate(e.target.value)
              }
            />
            <RespDistress
              value={respDistress}
              onChange={(e) => setRespDistress(e.target.value)}
            />
            <Spo2
              value={spo2}
              onChange={(e) => e.target.value >= 0 && setSpo2(e.target.value)}
            />
            <SuppO2
              value={suppO2}
              onChange={(e) => setSuppO2(e.target.value)}
            />
            {suppO2 === 'Yes' && (
              <>
                <SuppO2Fio2
                  value={suppO2Fio2}
                  onChange={(e) =>
                    e.target.value >= 0 && setSuppO2Fio2(e.target.value)
                  }
                />
                <RespSupport
                  value={respSupport}
                  onChange={(e) => setRespSupport(e.target.value)}
                />
              </>
            )}
            <HeartRate
              value={heartRate}
              onChange={(e) =>
                e.target.value >= 0 && setHeartRate(e.target.value)
              }
            />
            <BpSystolic
              value={bpSys}
              onChange={(e) => e.target.value >= 0 && setBpSys(e.target.value)}
            />
            <BpDiastolic
              value={bpDia}
              onChange={(e) => e.target.value >= 0 && setBpDia(e.target.value)}
            />
            {!bpSys && (
              <BpDerogation
                value={bpDerogation}
                onChange={(e) => setBpDerogation(e.target.value)}
              />
            )}
            <CapRefill
              value={capRefill}
              onChange={(e) =>
                e.target.value >= 0 && setCapRefill(e.target.value)
              }
            />

            <SectionHeader title="Other Observations" />
            <AVPU value={avpu} onChange={(e) => setAvpu(e.target.value)} />
            <Temperature
              value={temp}
              onChange={(e) => setTemp(e.target.value)}
            />
            <BpPosition
              value={bpPosition}
              onChange={(e) => setBpPosition(e.target.value)}
            />
            <GcsEyes
              value={gcsEyes}
              onChange={(e) => setGcsEyes(e.target.value)}
            />
            <GcsVerbal
              value={gcsVerbal}
              onChange={(e) => setGcsVerbal(e.target.value)}
            />
            <GcsMotor
              value={gcsMotor}
              onChange={(e) => setGcsMotor(e.target.value)}
            />
            <BGL
              value={bgl}
              onChange={(e) => e.target.value >= 0 && setBgl(e.target.value)}
            />
            <PainScore
              value={painScore}
              onChange={(e) =>
                e.target.value >= 0 && setPainScore(e.target.value)
              }
            />
            <PEFR
              value={pefr}
              onChange={(e) => e.target.value >= 0 && setPefr(e.target.value)}
            />
            <ETCO2
              value={etco2}
              onChange={(e) => e.target.value >= 0 && setEtco2(e.target.value)}
            />
          </>
        ) : (
          <>
            <AVPU value={avpu} onChange={(e) => setAvpu(e.target.value)} />
            <RespRate
              value={respRate}
              onChange={(e) =>
                e.target.value >= 0 && setRespRate(e.target.value)
              }
            />
            <Spo2
              value={spo2}
              onChange={(e) => e.target.value >= 0 && setSpo2(e.target.value)}
            />
            <SuppO2
              value={suppO2}
              onChange={(e) => setSuppO2(e.target.value)}
            />
            <HeartRate
              value={heartRate}
              onChange={(e) =>
                e.target.value >= 0 && setHeartRate(e.target.value)
              }
            />
            <BpSystolic
              value={bpSys}
              onChange={(e) => e.target.value >= 0 && setBpSys(e.target.value)}
            />
            <BpDiastolic
              value={bpDia}
              onChange={(e) => e.target.value >= 0 && setBpDia(e.target.value)}
            />
            <BpPosition
              value={bpPosition}
              onChange={(e) => setBpPosition(e.target.value)}
            />
            <Temperature
              value={temp}
              onChange={(e) => setTemp(e.target.value)}
            />
            <GcsEyes
              value={gcsEyes}
              onChange={(e) => setGcsEyes(e.target.value)}
            />
            <GcsVerbal
              value={gcsVerbal}
              onChange={(e) => setGcsVerbal(e.target.value)}
            />
            <GcsMotor
              value={gcsMotor}
              onChange={(e) => setGcsMotor(e.target.value)}
            />
            <BGL
              value={bgl}
              onChange={(e) => e.target.value >= 0 && setBgl(e.target.value)}
            />
            <CapRefill
              value={capRefill}
              onChange={(e) =>
                e.target.value >= 0 && setCapRefill(e.target.value)
              }
            />
            <PainScore
              value={painScore}
              onChange={(e) =>
                e.target.value >= 0 && setPainScore(e.target.value)
              }
            />
            <PEFR
              value={pefr}
              onChange={(e) => e.target.value >= 0 && setPefr(e.target.value)}
            />
            <ETCO2
              value={etco2}
              onChange={(e) => e.target.value >= 0 && setEtco2(e.target.value)}
            />
          </>
        )}
        <Grid item xs={12}>
          <Button
            variant="contained"
            size="small"
            color="success"
            type="submit"
            fullWidth
          >
            Submit
          </Button>
        </Grid>
      </Grid>
    </Paper>
  )
}

export default VitalSignsPopup
